// src/pages/Dashboard.js
import { useEffect, useMemo, useState, useContext } from "react";
import * as api from "../services/api";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend,
  BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";

const COLORS = ["#2563eb", "#16a34a", "#eab308", "#dc2626", "#6366f1"];

const idOf = (i) => i.interview_id ?? i.id ?? "-";
const schedOf = (i) => i.interview_scheduled_time ?? i.scheduled_time ?? null;
const statusOf = (i) => Number(i.interview_status ?? i.status ?? 0);

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const isSuper = user?.group_role === "superadmin";

  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState(api.getSelectedOrgId()); // null => All

  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [summary, setSummary] = useState({
    candidatesCount: 0,
    jobsCount: 0,
    scheduledInterviewsCount: 0,
  });

  const [languages, setLanguages] = useState([]); // [{ code, descr }]
  const langDescrByCode = useMemo(() => {
    const m = new Map();
    (languages || []).forEach((l) =>
      m.set(l.code ?? l.interview_language_code, l.descr ?? l.interview_language_descr)
    );
    return m;
  }, [languages]);

  // Load orgs for superadmin
  useEffect(() => {
    if (!isSuper) return;
    api
      .fetchOrganizations()
      .then((d) => setOrgs(Array.isArray(d) ? d : []))
      .catch(() => setOrgs([]));
  }, [isSuper]);

  const orgNameById = useMemo(() => {
    const m = new Map();
    (orgs || []).forEach((o) => m.set(o.id ?? o.organization_id, o.name ?? o.organization_name));
    return m;
  }, [orgs]);

  // pick up org changes coming from other pages (same localStorage key)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "selectedOrgId") setOrgFilter(api.getSelectedOrgId());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // FILTERS STATE
  const [filters, setFilters] = useState({ job: "", candidate: "", from: "", to: "" });

  // Reset filters whenever org changes
  useEffect(() => {
    setFilters({ job: "", candidate: "", from: "", to: "" });
  }, [orgFilter]);

  // ---- Scoped fetch helper: tries ?orgId, ?organizationId, ?organization_id; falls back to ?all=1 and filters client-side ----
  async function fetchScopedList(basePath) {
    if (!isSuper) {
      // normal users — just fetch
      return (await api.apiFetch(basePath)) ?? [];
    }
    if (orgFilter == null) {
      // superadmin, All orgs
      return (await api.apiFetch(`${basePath}?all=1`).catch(() => [])) ?? [];
    }
    // Try with orgId
    let data = await api.apiFetch(`${basePath}?orgId=${orgFilter}`).catch(() => null);
    if (Array.isArray(data) && data.length) return data;

    // Try alternative param names (some controllers might expect these)
    data = await api.apiFetch(`${basePath}?organizationId=${orgFilter}`).catch(() => null);
    if (Array.isArray(data) && data.length) return data;

    data = await api.apiFetch(`${basePath}?organization_id=${orgFilter}`).catch(() => null);
    if (Array.isArray(data) && data.length) return data;

    // Fallback: get everything and filter client-side by any org key we find
    data = await api.apiFetch(`${basePath}?all=1`).catch(() => []);
    const orgKey = (row) =>
      row.organization_id ?? row.org_id ?? row.orgId ?? row.organizationId ?? null;
    return (Array.isArray(data) ? data : []).filter((row) => Number(orgKey(row)) === Number(orgFilter));
  }

  // Load lists for cards/charts/tables
  useEffect(() => {
    let cancelled = false;
    async function loadLists() {
      try {
        const [j, c, i, l] = await Promise.all([
          fetchScopedList(`/api/jobs`),
          fetchScopedList(`/api/candidates`),
          fetchScopedList(`/api/interviews`),
          api.apiFetch(`/api/interviews/languages`),
        ]);
        if (cancelled) return;
        setJobs(Array.isArray(j) ? j : []);
        setCandidates(Array.isArray(c) ? c : []);
        setInterviews(Array.isArray(i) ? i : []);
        setLanguages(Array.isArray(l) ? l : []);
      } catch (e) {
        if (!cancelled) {
          console.error("Dashboard lists load error:", e);
          setJobs([]);
          setCandidates([]);
          setInterviews([]);
          setLanguages([]);
        }
      }
    }
    loadLists();
    return () => {
      cancelled = true;
    };
  }, [isSuper, orgFilter]);

  // Analytics summary for the header cards
  useEffect(() => {
    let cancelled = false;
    async function loadSummary() {
      try {
        let data;
        if (isSuper) {
          const qs = orgFilter == null ? "?all=1" : `?orgId=${orgFilter}`;
          // also try alt param on failure
          data = await api.apiFetch(`/api/analytics/summary${qs}`).catch(async () => {
            const alt = orgFilter == null
              ? "/api/analytics/summary?all=1"
              : `/api/analytics/summary?organizationId=${orgFilter}`;
            return api.apiFetch(alt);
          });
        } else {
          data = await api.apiFetch(`/api/analytics/summary`);
        }
        if (!cancelled) {
          setSummary({
            candidatesCount: data?.candidatesCount ?? data?.candidates ?? 0,
            jobsCount: data?.jobsCount ?? data?.jobs ?? 0,
            scheduledInterviewsCount: data?.scheduledInterviewsCount ?? data?.interviews ?? 0,
          });
        }
      } catch (e) {
        if (!cancelled)
          setSummary({ candidatesCount: 0, jobsCount: 0, scheduledInterviewsCount: 0 });
      }
    }
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [isSuper, orgFilter]);

  const candidateNameById = useMemo(() => {
    const map = new Map();
    (candidates || []).forEach((c) => {
      map.set(c.id ?? c.candidate_id, c.name ?? c.candidate_name);
    });
    return map;
  }, [candidates]);

  const jobTitleById = useMemo(() => {
    const map = new Map();
    (jobs || []).forEach((j) => {
      map.set(j.id ?? j.job_req_id, j.title ?? j.job_req_title);
    });
    return map;
  }, [jobs]);

  const interviewsNormalized = (interviews || []).map((i) => ({
    id: idOf(i),
    candidate_id: i.candidate_id,
    candidate_name: i.candidate_name,
    job_req_id: i.job_req_id,
    job_req_title: i.job_req_title,
    title: i.title ?? i.job_req_title,
    language:
      i.interview_language_descr ||
      langDescrByCode.get(i.interview_language_code) ||
      i.interview_language_code,
    scheduled_time: schedOf(i),
    status: statusOf(i),
    organization_id: i.organization_id,
  }));

  // Only shrink counts when job/candidate filters are used (date-only filters should not zero the cards)
  const hasCountFilter = Boolean(filters.job || filters.candidate);

  const filteredInterviews = useMemo(() => {
    let data = Array.isArray(interviewsNormalized) ? interviewsNormalized : [];
    if (filters.job) data = data.filter((i) => i.job_req_id === Number(filters.job));
    if (filters.candidate) data = data.filter((i) => i.candidate_id === Number(filters.candidate));
    if (filters.from)
      data = data.filter(
        (i) => i.scheduled_time && new Date(i.scheduled_time) >= new Date(filters.from)
      );
    if (filters.to)
      data = data.filter(
        (i) => i.scheduled_time && new Date(i.scheduled_time) <= new Date(filters.to)
      );
    return data;
  }, [filters, interviewsNormalized]);

  const visibleCandidateIds = new Set((filteredInterviews || []).map((i) => i.candidate_id));
  const visibleJobIds = new Set((filteredInterviews || []).map((i) => i.job_req_id));

  // For charts, if a Job/Candidate is selected we show only the involved ones.
  // If only date filters (or none) are used, show all for the selected org.
  const visibleCandidates = hasCountFilter
    ? (candidates || []).filter((c) => visibleCandidateIds.has(c.id ?? c.candidate_id))
    : candidates || [];

  const visibleJobs = hasCountFilter
    ? (jobs || []).filter((j) => visibleJobIds.has(j.id ?? j.job_req_id))
    : jobs || [];

  // ---- totals ----
  const totalCandidatesDisplay = hasCountFilter
    ? visibleCandidates.length
    : (summary?.candidatesCount ?? 0);

  const totalJobsDisplay = hasCountFilter
    ? visibleJobs.length
    : (summary?.jobsCount ?? 0);

  const totalInterviewsDisplay =
    (filteredInterviews && filteredInterviews.length > 0)
      ? filteredInterviews.length
      : (summary?.scheduledInterviewsCount ?? 0);

  // ---------- CHART DATA ----------
  const candidateStatusData = [
    {
      name: "Active",
      value: visibleCandidates.filter((c) => Number(c.status ?? c.candidate_status) === 1).length,
    },
    {
      name: "Inactive",
      value: visibleCandidates.filter((c) => Number(c.status ?? c.candidate_status) === 0).length,
    },
  ];

  const jobStatusData = [
    {
      name: "Open",
      value: visibleJobs.filter((j) => Number(j.status ?? j.job_req_status) === 1).length,
    },
    {
      name: "On Hold",
      value: visibleJobs.filter((j) => Number(j.status ?? j.job_req_status) === 2).length,
    },
    {
      name: "Cancelled",
      value: visibleJobs.filter((j) => Number(j.status ?? j.job_req_status) === 0).length,
    },
  ];

  const interviewsPerDay = (filteredInterviews || [])
    .map((i) => ({
      date: i.scheduled_time ? new Date(i.scheduled_time).toLocaleDateString() : "N/A",
      scheduled: i.status === 1 ? 1 : 0,
      completed: i.status === 2 ? 1 : 0,
      cancelled: i.status === 0 ? 1 : 0,
    }))
    .reduce((acc, { date, scheduled, completed, cancelled }) => {
      const found = acc.find((a) => a.date === date);
      if (found) {
        found.scheduled += scheduled;
        found.completed += completed;
        found.cancelled += cancelled;
      } else {
        acc.push({ date, scheduled, completed, cancelled });
      }
      return acc;
    }, []);

  // CSV/PDF export
  const csvHeaders = [
    { label: "Interview ID", key: "id" },
    { label: "Candidate ID", key: "candidate_id" },
    { label: "Job Req ID", key: "job_req_id" },
    { label: "Title", key: "title" },
    { label: "Language", key: "language" },
    { label: "Scheduled Time", key: "scheduled_time" },
    { label: "Status", key: "status" },
    ...(isSuper ? [{ label: "Organization", key: "organization_name" }] : []),
  ];

  const exportRows = (filteredInterviews || []).map((i) => ({
    ...i,
    status: i.status === 1 ? "Scheduled" : i.status === 2 ? "Completed" : "Cancelled",
    organization_name:
      orgNameById.get(i.organization_id) ??
      (i.organization_id != null ? `#${i.organization_id}` : "-"),
  }));

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Interviews Report", 14, 10);
    doc.autoTable({
      startY: 18,
      head: [csvHeaders.map((h) => h.label)],
      body: exportRows.map((row) => csvHeaders.map((h) => row[h.key] ?? "")),
    });
    doc.save("interviews.pdf");
  };

  const handleOrgFilterChange = (e) => {
    const val = e.target.value;
    const next = val === "" ? null : Number(val);
    setOrgFilter(next);
    api.setSelectedOrgId(next);
  };

  return (
    <div>
      <Navbar />
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-4">Dashboard Analytics</h2>

        {/* FILTERS */}
        <div className="flex flex-wrap items-end gap-3 mb-8">
          {isSuper && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Organization</label>
              <select
                className="border p-2 rounded"
                value={orgFilter ?? ""}
                onChange={handleOrgFilterChange}
              >
                <option value="">All Organizations</option>
                {orgs.map((o) => (
                  <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                    {o.name ?? o.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Job</label>
            <select
              className="border p-2 rounded"
              value={filters.job}
              onChange={(e) => setFilters((f) => ({ ...f, job: e.target.value }))}
            >
              <option value="">All Jobs</option>
              {(jobs || []).map((j) => (
                <option key={j.id ?? j.job_req_id} value={j.id ?? j.job_req_id}>
                  {j.title ?? j.job_req_title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Candidate</label>
            <select
              className="border p-2 rounded"
              value={filters.candidate}
              onChange={(e) => setFilters((f) => ({ ...f, candidate: e.target.value }))}
            >
              <option value="">All Candidates</option>
              {(candidates || []).map((c) => (
                <option key={c.id ?? c.candidate_id} value={c.id ?? c.candidate_id}>
                  {c.name ?? c.candidate_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Start Date</label>
            <input
              type="date"
              className="border p-2 rounded"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">End Date</label>
            <input
              type="date"
              className="border p-2 rounded"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            />
          </div>
        </div>

        {/* CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded shadow p-4">
            <div className="text-sm text-gray-500">Total Candidates</div>
            <div className="text-2xl font-bold">{totalCandidatesDisplay}</div>
          </div>
          <div className="bg-white rounded shadow p-4">
            <div className="text-sm text-gray-500">Open Jobs</div>
            <div className="text-2xl font-bold">{totalJobsDisplay}</div>
          </div>
          <div className="bg-white rounded shadow p-4">
            <div className="text-sm text-gray-500">Scheduled Interviews</div>
            <div className="text-2xl font-bold">{totalInterviewsDisplay}</div>
          </div>
        </div>

        {/* CHARTS */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded shadow p-4">
            <h3 className="font-semibold mb-2">Candidates by Status</h3>
            <PieChart width={320} height={220}>
              <Pie data={candidateStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {candidateStatusData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <ReTooltip />
              <Legend />
            </PieChart>
          </div>

          <div className="bg-white rounded shadow p-4">
            <h3 className="font-semibold mb-2">Jobs by Status</h3>
            <PieChart width={320} height={220}>
              <Pie data={jobStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {jobStatusData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <ReTooltip />
              <Legend />
            </PieChart>
          </div>

          <div className="bg-white rounded shadow p-4">
            <h3 className="font-semibold mb-2">Interviews per Day</h3>
            <BarChart width={380} height={220} data={interviewsPerDay}>
              <XAxis dataKey="date" />
              <YAxis />
              <ReTooltip />
              <Legend />
              {/* Color-coded bars */}
              <Bar dataKey="scheduled" stackId="a" fill="#2563eb" name="Scheduled" />
              <Bar dataKey="completed" stackId="a" fill="#16a34a" name="Completed" />
              <Bar dataKey="cancelled" stackId="a" fill="#dc2626" name="Cancelled" />
            </BarChart>
          </div>
        </div>

        {/* EXPORTS */}
        <div className="flex gap-3 mb-4">
          <button className="bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700">
            <CSVLink
              headers={[
                { label: "Interview ID", key: "id" },
                { label: "Candidate ID", key: "candidate_id" },
                { label: "Job Req ID", key: "job_req_id" },
                { label: "Title", key: "title" },
                { label: "Language", key: "language" },
                { label: "Scheduled Time", key: "scheduled_time" },
                { label: "Status", key: "status" },
                ...(isSuper ? [{ label: "Organization", key: "organization_name" }] : []),
              ]}
              data={(filteredInterviews || []).map((i) => ({
                ...i,
                status: i.status === 1 ? "Scheduled" : i.status === 2 ? "Completed" : "Cancelled",
                organization_name:
                  orgNameById.get(i.organization_id) ??
                  (i.organization_id != null ? `#${i.organization_id}` : "-"),
              }))}
              filename="interviews.csv"
              className="text-white"
            >
              Export CSV
            </CSVLink>
          </button>
          <button onClick={exportPDF} className="bg-sky-600 text-white px-3 py-1 rounded hover:bg-sky-700">
            Export PDF
          </button>
        </div>

        {/* TABLE */}
        <h3 className="text-lg font-semibold mb-2">Filtered Interviews</h3>
        <table className="w-full bg-white rounded shadow text-left">
          <thead>
            <tr>
              <th className="p-2 border-b">Interview ID</th>
              <th className="p-2 border-b">Candidate</th>
              <th className="p-2 border-b">Job ID</th>
              <th className="p-2 border-b">Title</th>
              <th className="p-2 border-b">Language</th>
              <th className="p-2 border-b">Scheduled Time</th>
              <th className="p-2 border-b">Status</th>
              {isSuper && <th className="p-2 border-b">Organization</th>}
            </tr>
          </thead>
          <tbody>
            {(filteredInterviews || []).map((i) => (
              <tr key={i.id} className="hover:bg-gray-100">
                <td className="p-2 border-b">{i.id}</td>
                <td className="p-2 border-b">
                  {candidateNameById.get(i.candidate_id) ?? i.candidate_name ?? i.candidate_id}
                </td>
                <td className="p-2 border-b">{i.job_req_id}</td>
                <td className="p-2 border-b">{jobTitleById.get(i.job_req_id) ?? i.job_req_title ?? "-"}</td>
                <td className="p-2 border-b">{i.language ?? "-"}</td>
                <td className="p-2 border-b">
                  {i.scheduled_time ? new Date(i.scheduled_time).toLocaleString() : ""}
                </td>
                <td className="p-2 border-b">
                  {i.status === 1 ? "Scheduled" : i.status === 2 ? "Completed" : "Cancelled"}
                </td>
                {isSuper && (
                  <td className="p-2 border-b">
                    {orgNameById.get(i.organization_id) ??
                      (i.organization_id != null ? `#${i.organization_id}` : "-")}
                  </td>
                )}
              </tr>
            ))}
            {(!filteredInterviews || filteredInterviews.length === 0) && (
              <tr>
                <td className="p-3 text-gray-500" colSpan={isSuper ? 8 : 7}>
                  No interviews
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
